/**
 * EvangelismCheckin.js — Weekly Evangelism Check-in (Disciple-Making pillar)
 *
 * Exports:
 *   <EvangelismCheckinProvider>  — wrap around app; opens modal on ?evangelism=1
 *   useEvangelismCheckin()       — { openCheckin } for manual trigger (Grow card)
 *   <EvangelismCard>             — prompt/summary card for the Grow hub
 *   <EvangelismStats>            — streak + patterns block for the profile
 *
 * Mirrors the FaithCheckin feature: a deep-linked modal triggered by a per-user
 * Sunday-evening push, a private celebration screen, and a profile stats block.
 */

import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flame, Plus, X, ChevronRight, Search, Check, HeartHandshake,
  MessageCircle, Church, BookOpen, Heart, HandHeart, Users,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { evangelismApi } from '../../api/client';
import './EvangelismCheckin.css';

// ── Constants ────────────────────────────────────────────────────────────────

export const SHARE_TYPES = ['conversation', 'invite', 'gospel', 'prayer', 'serve'];

const SHARE_ICONS = {
  conversation: MessageCircle,
  invite:       Church,
  gospel:       BookOpen,
  prayer:       Heart,
  serve:        HandHeart,
};

const SHARE_COLORS = {
  conversation: '#2563EB',
  invite:       '#E8751A',
  gospel:       '#16A34A',
  prayer:       '#8B5CF6',
  serve:        '#0D9488',
};

// ── Context ──────────────────────────────────────────────────────────────────

const EvangelismContext = createContext(null);
export function useEvangelismCheckin() {
  return useContext(EvangelismContext) || {};
}

// ── Provider (deep-link only; the Sunday push is the nudge) ───────────────────

export function EvangelismCheckinProvider({ children }) {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('evangelism') === '1') {
      setShowModal(true);
      const clean = window.location.pathname + (window.location.search.replace(/[?&]evangelism=1/, '') || '');
      window.history.replaceState({}, '', clean);
    }
  }, []);

  return (
    <EvangelismContext.Provider value={{ openCheckin: () => setShowModal(true) }}>
      {children}
      {showModal && <EvangelismCheckinModal onClose={() => setShowModal(false)} />}
    </EvangelismContext.Provider>
  );
}

// ── Share-type selector ───────────────────────────────────────────────────────

function ShareTypeChips({ value, onChange, t }) {
  return (
    <div className="ev-type-chips">
      {SHARE_TYPES.map(type => {
        const Icon = SHARE_ICONS[type];
        const active = value === type;
        return (
          <button
            key={type}
            type="button"
            className={`ev-type-chip ${active ? 'active' : ''}`}
            style={active ? { background: SHARE_COLORS[type], borderColor: SHARE_COLORS[type] } : {}}
            onClick={() => onChange(type)}
          >
            <Icon size={13}/> {t(`shareTypes.${type}`)}
          </button>
        );
      })}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

function EvangelismCheckinModal({ onClose, onSaved }) {
  const { t } = useTranslation('evangelism');
  const navigate = useNavigate();

  const [people, setPeople]       = useState([]);    // {key, personName, connectionUserId, recipientId, shareType, note}
  const [suggestions, setSuggest] = useState([]);
  const [nameInput, setNameInput] = useState('');
  const [overallNote, setNote]    = useState('');
  const [saving, setSaving]       = useState(false);
  const [done, setDone]           = useState(false);
  const [result, setResult]       = useState(null);  // saved checkin (shares carry ids)
  const [streak, setStreak]       = useState(0);
  const [addedToPrayer, setAdded] = useState({});    // shareId -> true

  // Load this week's existing check-in (so re-opening edits, not duplicates).
  useEffect(() => {
    evangelismApi.current()
      .then(d => {
        setSuggest(d.suggestions || []);
        if (d.checkin) {
          setNote(d.checkin.note || '');
          setPeople((d.checkin.shares || []).map(s => ({
            key:              s.id,
            personName:       s.personName,
            connectionUserId: s.connectionUserId,
            recipientId:      s.recipientId,
            shareType:        s.shareType,
            note:             s.note || '',
          })));
        }
      })
      .catch(() => {});
  }, []);

  const usedKeys = new Set(people.map(p => p.recipientId || p.connectionUserId || p.personName.toLowerCase()));
  const filteredSuggestions = suggestions
    .filter(s => !usedKeys.has(s.recipientId || s.connectionUserId || s.personName.toLowerCase()))
    .filter(s => !nameInput.trim() || s.personName.toLowerCase().includes(nameInput.trim().toLowerCase()))
    .slice(0, 6);

  function addPerson(p) {
    setPeople(prev => [...prev, {
      key:              p.recipientId || p.connectionUserId || `n-${Date.now()}`,
      personName:       p.personName,
      connectionUserId: p.connectionUserId || null,
      recipientId:      p.recipientId || null,
      shareType:        'conversation',
      note:             '',
    }]);
    setNameInput('');
  }

  function addTypedName() {
    const name = nameInput.trim();
    if (!name) return;
    addPerson({ personName: name });
  }

  function updatePerson(key, patch) {
    setPeople(prev => prev.map(p => p.key === key ? { ...p, ...patch } : p));
  }

  function removePerson(key) {
    setPeople(prev => prev.filter(p => p.key !== key));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const { checkin } = await evangelismApi.save({
        note:   overallNote.trim() || undefined,
        shares: people.map(p => ({
          personName:       p.personName,
          connectionUserId: p.connectionUserId,
          recipientId:      p.recipientId,
          shareType:        p.shareType,
          note:             p.note?.trim() || undefined,
        })),
      });
      setResult(checkin);
      // Pull the fresh streak for the celebration line.
      try { const s = await evangelismApi.list(12); setStreak(s.streak || 0); } catch {}
      setDone(true);
    } catch {
      setSaving(false);
    }
  }

  async function handleSkip() {
    try { await evangelismApi.save({ skipped: true }); } catch {}
    (onSaved || onClose)();
  }

  async function addToPrayer(shareId) {
    if (!shareId || addedToPrayer[shareId]) return;
    try {
      await evangelismApi.shareToPrayer(shareId);
      setAdded(prev => ({ ...prev, [shareId]: true }));
    } catch {}
  }

  function shareTestimony() {
    const names = (result?.shares || []).map(s => s.personName).join(', ');
    const seed  = t('testimonySeed', names);
    (onSaved || onClose)();
    navigate(`/?compose=praise&text=${encodeURIComponent(seed)}`);
  }

  // ── Done / celebration screen ───────────────────────────────────────────────
  if (done) {
    const savedShares = result?.shares || [];
    return (
      <div className="ev-overlay" onClick={e => e.target === e.currentTarget && (onSaved || onClose)()}>
        <div className="ev-modal ev-modal-done">
          <div className="ev-done-flame"><Flame size={40} color="#E8751A"/></div>
          <h2 className="ev-done-title">{savedShares.length ? t('doneTitle') : t('doneEmptyTitle')}</h2>
          <p className="ev-done-sub">
            {savedShares.length ? t('donePeople', savedShares.length) : t('doneEmptySub')}
          </p>
          {streak > 1 && <div className="ev-streak-badge">{t('streakLabel', streak)}</div>}

          {savedShares.length > 0 && (
            <div className="ev-done-people">
              {savedShares.map(s => (
                <div key={s.id} className="ev-done-person">
                  <span className="ev-done-person-name">{s.personName}</span>
                  <button
                    className={`ev-prayer-btn ${addedToPrayer[s.id] ? 'added' : ''}`}
                    onClick={() => addToPrayer(s.id)}
                    disabled={addedToPrayer[s.id]}
                  >
                    {addedToPrayer[s.id] ? <><Check size={13}/> {t('addedToPrayer')}</> : <><Heart size={13}/> {t('addToPrayer')}</>}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="ev-done-actions">
            {savedShares.length > 0 && (
              <button className="ev-share-testimony" onClick={shareTestimony}>
                <HeartHandshake size={15}/> {t('shareTestimony')}
              </button>
            )}
            <button className="btn btn-primary" onClick={() => (onSaved || onClose)()}>{t('doneClose')}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Entry screen ────────────────────────────────────────────────────────────
  return (
    <div className="ev-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ev-modal">
        <div className="ev-modal-header">
          <div>
            <h2 className="ev-modal-title"><Flame size={18} color="#E8751A"/> {t('modalTitle')}</h2>
            <p className="ev-modal-sub">{t('modalSub')}</p>
          </div>
          <button className="ev-close" onClick={onClose}><X size={18}/></button>
        </div>

        {/* Added people */}
        {people.length > 0 && (
          <div className="ev-people">
            {people.map(p => (
              <div key={p.key} className="ev-person">
                <div className="ev-person-top">
                  <span className="ev-person-name">{p.personName}</span>
                  <button className="ev-person-remove" onClick={() => removePerson(p.key)}><X size={14}/></button>
                </div>
                <ShareTypeChips value={p.shareType} onChange={st => updatePerson(p.key, { shareType: st })} t={t}/>
              </div>
            ))}
          </div>
        )}

        {/* Add a person */}
        <div className="ev-add-row">
          <Search size={15} className="ev-add-icon"/>
          <input
            className="ev-add-input"
            type="text"
            placeholder={t('addPlaceholder')}
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTypedName(); } }}
          />
          {nameInput.trim() && (
            <button className="ev-add-btn" onClick={addTypedName}><Plus size={15}/></button>
          )}
        </div>

        {/* Suggestions */}
        {filteredSuggestions.length > 0 && (
          <div className="ev-suggestions">
            {filteredSuggestions.map(s => (
              <button key={s.recipientId || s.personName} className="ev-suggestion" onClick={() => addPerson(s)}>
                <Plus size={12}/> {s.personName}
              </button>
            ))}
          </div>
        )}

        {/* Overall note */}
        <textarea
          className="ev-note"
          placeholder={t('notePlaceholder')}
          value={overallNote}
          onChange={e => setNote(e.target.value)}
          rows={2}
          maxLength={500}
        />

        <div className="ev-actions">
          <button className="ev-skip" onClick={handleSkip}>{t('skip')}</button>
          <button className="ev-save btn btn-primary" disabled={saving || people.length === 0} onClick={handleSave}>
            {saving
              ? <span className="spinner" style={{ width: 14, height: 14, borderColor: 'white', borderTopColor: 'transparent' }}/>
              : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Grow-hub card ─────────────────────────────────────────────────────────────

export function EvangelismCard() {
  const { t } = useTranslation('evangelism');
  const { openCheckin } = useEvangelismCheckin();
  const [checkin, setCheckin] = useState(null);
  const [loaded, setLoaded]   = useState(false);

  const load = useCallback(() => {
    evangelismApi.current()
      .then(d => setCheckin(d.checkin || null))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!loaded) return null;

  const count = checkin && !checkin.skipped ? (checkin.shares?.length || 0) : 0;
  const done  = checkin && (checkin.skipped || count > 0);

  return (
    <div className="card ev-card" onClick={openCheckin}>
      <div className="ev-card-flame"><Flame size={20} color="white"/></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="ev-card-title">{t('cardTitle')}</div>
        <div className="ev-card-sub">
          {done
            ? (count > 0 ? t('cardDone', count) : t('cardSkipped'))
            : t('cardPrompt')}
        </div>
      </div>
      <ChevronRight size={18} color="var(--text-lt)"/>
    </div>
  );
}

// ── Profile stats block ───────────────────────────────────────────────────────

export function EvangelismStats() {
  const { t } = useTranslation('evangelism');
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const { openCheckin }     = useEvangelismCheckin();

  useEffect(() => {
    evangelismApi.list(26)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoad(false));
  }, []);

  if (loading) return null;
  const stats = data?.stats;
  const streak = data?.streak || 0;
  const hasData = stats && stats.weeksActive > 0;

  if (!hasData) {
    return (
      <div className="ev-stats-empty card" onClick={openCheckin}>
        <Flame size={22} color="#E8751A"/>
        <div>
          <div className="ev-stats-empty-title">{t('statsTitle')}</div>
          <div className="ev-stats-empty-sub">{t('emptyHint')}</div>
        </div>
        <ChevronRight size={18} color="var(--text-lt)" style={{ marginLeft: 'auto' }}/>
      </div>
    );
  }

  const maxType = Math.max(1, ...SHARE_TYPES.map(ty => stats.byShareType[ty] || 0));

  return (
    <div className="ev-stats card">
      <h3 className="ev-stats-heading"><Flame size={16} color="#E8751A"/> {t('statsTitle')}</h3>

      <div className="ev-stat-row">
        <div className="ev-stat">
          <div className="ev-stat-val">{stats.totalPeople}</div>
          <div className="ev-stat-label">{t('statPeople')}</div>
        </div>
        <div className="ev-stat">
          <div className="ev-stat-val">{stats.weeksActive}</div>
          <div className="ev-stat-label">{t('statWeeks')}</div>
        </div>
        <div className="ev-stat">
          <div className="ev-stat-val ev-stat-streak">{streak}</div>
          <div className="ev-stat-label">{t('statStreak')}</div>
        </div>
      </div>

      {/* How you tend to share */}
      <div className="ev-breakdown">
        {SHARE_TYPES.map(ty => {
          const n = stats.byShareType[ty] || 0;
          return (
            <div key={ty} className="ev-breakdown-row">
              <span className="ev-breakdown-label" style={{ color: SHARE_COLORS[ty] }}>{t(`shareTypes.${ty}`)}</span>
              <div className="ev-breakdown-bar">
                <div className="ev-breakdown-fill" style={{ width: `${(n / maxType) * 100}%`, background: SHARE_COLORS[ty] }}/>
              </div>
              <span className="ev-breakdown-count">{n}</span>
            </div>
          );
        })}
      </div>

      {/* People you keep investing in */}
      {stats.recurringPeople.length > 0 && (
        <>
          <h4 className="ev-recurring-title"><Users size={14}/> {t('recurringTitle')}</h4>
          <div className="ev-recurring">
            {stats.recurringPeople.map(p => (
              <div key={p.key} className="ev-recurring-row">
                <span className="ev-recurring-name">{p.personName}</span>
                <span className="ev-recurring-weeks">{t('weeksCount', p.weeks)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
